import { apiClient } from '@lib/api/client';
import { MESSAGES } from '@lib/constants/messages';
import type {
  User,
  UserListResponse,
  UserSearchParams,
  AludraUser,
} from '../types/user.types';

/**
 * Map Aludra API user to frontend user format
 */
function mapAludraUserToUser(aludraUser: any): User {
  // Map roles from UserRoles array (from Aludra API)
  const userRoles = aludraUser.UserRoles?.map((ur: any) => ur.RoleName) || [];
  const roles = userRoles.length > 0 ? userRoles : (aludraUser.Roles || (aludraUser.Role ? [aludraUser.Role] : []));

  // Get company info from Companies array
  const company = aludraUser.Companies?.[0] || {};

  // Map all assigned companies (multi-compañía)
  const companies = (aludraUser.Companies || [])
    .map((c: any) => ({
      id: c.CompanyId || c.Id,
      name: c.Name || c.CompanyName,
      code: c.Code || c.CompanyCode,
    }))
    .filter((c: any) => c.id);

  // Map branch offices - extract IDs from BranchOffice array
  const branchOffices = aludraUser.BranchOffice?.map((bo: any) =>
    bo.BranchOfficeId || bo.Id || bo.id
  ).filter((id: any) => id) || [];

  return {
    id: aludraUser.Id || aludraUser.UserId,
    email: aludraUser.Email || aludraUser.UserName,
    idNumber: aludraUser.IDNumber || '',
    firstName: aludraUser.FirstName || '',
    lastName: aludraUser.LastName || '',
    role: roles[0] || 'USER',
    roles: roles,
    companyId: company.CompanyId || aludraUser.CompanyId || '',
    companyName: company.Name || aludraUser.CompanyName || '',
    companyCode: company.Code || aludraUser.CompanyCode || '',
    companies: companies.length > 0 ? companies : undefined,
    avatar: aludraUser.Avatar || aludraUser.ProfileImage || null,
    phone: aludraUser.Phone || aludraUser.PhoneNumber || '',
    isActive: aludraUser.Status !== undefined ? aludraUser.Status : (aludraUser.IsActive !== undefined ? aludraUser.IsActive : true),
    branchOffices: branchOffices.length > 0 ? branchOffices : undefined,
    createdAt: aludraUser.CreatedAt || new Date().toISOString(),
    updatedAt: aludraUser.UpdatedAt || new Date().toISOString(),
  };
}

export const userService = {
  /**
   * Search users from Aludra API
   * The backend will automatically use the selected company
   */
  async getUsers(params?: {
    page?: number;
    pageSize?: number;
    search?: string;
    role?: string;
  }): Promise<UserListResponse> {
    // Paginación y búsqueda reales contra Aludra — el backend ya reenvía
    // SearchText/PageNumber/PageSize tal cual (ver user.controller.ts::searchUsers).
    const page = params?.page || 1;
    const pageSize = Math.min(params?.pageSize || 10, 100); // el backend clampea a 100 de todos modos
    const searchParams: Partial<UserSearchParams> = {
      SearchText: params?.search?.trim() || null,
      PageNumber: page,
      PageSize: pageSize,
      // No Roles filter — Aludra role names vary by tenant; sending a hardcoded list
      // causes the search to return 0 results when names don't match exactly.
    };

    console.log('🔍 Searching users with params:', searchParams);

    const response = await apiClient.post<{
      status: string;
      data: {
        Data: AludraUser[];
      };
      total?: number;
    }>('/users/search', searchParams);

    console.log('📥 Search response:', response.data);

    const aludraUsers = response.data.data.Data || [];
    console.log('📥 Raw Aludra users:', aludraUsers);

    const allUsers = aludraUsers.map(mapAludraUserToUser);
    const total = response.data.total ?? allUsers.length;

    console.log('📊 Users fetched:', allUsers.length, 'of total', total);
    console.log('👥 Mapped users:', allUsers.map(u => ({ email: u.email, roles: u.roles })));

    return {
      users: allUsers,
      total,
      page,
      pageSize,
      hasMorePages: page * pageSize < total,
    };
  },

  /**
   * Ficha completa del usuario por Id. A diferencia de getUsers() (que lista
   * usuarios en el contexto de la compañía activa y puede recortar
   * Companies/BranchOffice a esa compañía nada más), esta consulta trae el
   * registro real y completo del usuario — es la fuente correcta para
   * poblar el modal de edición.
   */
  async getUserById(id: string): Promise<User> {
    const response = await apiClient.get<{
      status: string;
      data: AludraUser;
    }>(`/users/${id}`);

    if (!response.data.data) {
      throw new Error(MESSAGES.USER_NOT_FOUND);
    }

    return mapAludraUserToUser(response.data.data);
  },

  async createUser(user: Omit<Partial<User>, 'companies'> & {
    email: string;
    idNumber: string;
    firstName: string;
    lastName: string;
    roles: string[];
    branchOffices: string[];
    companies: string[];
  }): Promise<User> {
    const payload = {
      Email: user.email,
      IDNumber: user.idNumber,
      FirstName: user.firstName,
      LastName: user.lastName,
      RolesName: user.roles,
      BranchOffice: user.branchOffices.map((id) => ({ BranchOfficeId: id })),
      Companies: user.companies,
    };

    console.log('📤 Creating user with payload:', payload);
    console.log('📤 Roles being sent:', user.roles);

    const response = await apiClient.post<{
      status: string;
      message: string;
      data: any;
    }>('/users', payload);

    console.log('✅ Create user response:', response.data);

    // Return a basic user object - the actual data will be fetched in the refresh
    return {
      id: response.data.data?.Id || '',
      email: user.email,
      idNumber: user.idNumber,
      firstName: user.firstName,
      lastName: user.lastName,
      role: user.roles[0],
      roles: user.roles,
      companyId: '',
      companyName: '',
      companyCode: '',
      avatar: null,
      phone: '',
      isActive: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  },

  /**
   * Compañías asignables al crear/editar un usuario. A diferencia de
   * companyService.getCompanies() (cacheada desde el login y potencialmente
   * incompleta — ver authService.getAssignableCompaniesFresh en el backend),
   * esta consulta siempre trae la lista fresca de Aludra.
   */
  async getAssignableCompanies(): Promise<Array<{ id: string; name: string | null; fullName?: string | null; code: string }>> {
    const response = await apiClient.get<{
      status: string;
      data: Array<{ id: string; name: string | null; fullName?: string | null; code: string }>;
    }>('/users/assignable-companies');

    return response.data.data;
  },

  async getRoles(): Promise<Array<{ id: string; name: string; description: string }>> {
    const response = await apiClient.get<{
      status: string;
      data: Array<{ id: string; name: string; description: string }>;
    }>('/users/roles');

    return response.data.data;
  },

  async getBranchOffices(): Promise<Array<{ Id: string; Name: string; Code: string }>> {
    const response = await apiClient.get<{
      branchOffices: Array<any>;
      total: number;
      page: number;
      pageSize: number;
    }>('/companies/branch-offices');

    console.log('🏪 Branch offices response:', response.data);
    console.log('🏪 Branch offices data:', response.data.branchOffices);
    console.log('🏪 Branch offices count:', response.data.branchOffices?.length || 0);

    // Map the branch offices to the format expected by the UI
    return response.data.branchOffices.map((bo: any) => ({
      Id: bo.id,
      Name: bo.name,
      Code: bo.code,
    }));
  },

  async updateUser(id: string, user: Omit<Partial<User>, 'companies'> & {
    email: string;
    idNumber: string;
    firstName: string;
    lastName: string;
    roles: string[];
    branchOffices: string[];
    companies: string[];
    password?: string;
  }): Promise<User> {
    await apiClient.put<{
      status: string;
      message: string;
      data: any;
    }>(`/users/${id}`, {
      Email: user.email,
      IDNumber: user.idNumber,
      FirstName: user.firstName,
      LastName: user.lastName,
      RolesName: user.roles,
      BranchOffice: user.branchOffices.map((boId) => ({ BranchOfficeId: boId })),
      Companies: user.companies,
      ...(user.password ? { Password: user.password } : {}),
    });

    // Return a basic user object - the actual data will be fetched in the refresh
    return {
      id: id,
      email: user.email,
      idNumber: user.idNumber,
      firstName: user.firstName,
      lastName: user.lastName,
      role: user.roles[0],
      roles: user.roles,
      companyId: '',
      companyName: '',
      companyCode: '',
      avatar: null,
      phone: '',
      isActive: true,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
  },

  async deleteUser(id: string): Promise<void> {
    await apiClient.delete(`/users/${id}`);
  },

  async reactivateUser(id: string): Promise<void> {
    await apiClient.post(`/users/${id}/reactivate`);
  },

  async resendInvitation(email: string): Promise<void> {
    await apiClient.post('/users/resend-invitation', { email });
  },
};
